/*
 * Localization popup (Notbranded)
 * Suggests the store for the visitor's detected country instead of an automatic
 * redirect. See sections/custom-localization-popup.liquid.
 *
 * Flow:
 * 1. Skip if the visitor already closed the popup / chose to stay (cookie).
 * 2. Detect the country via Shopify's /browsing_context_suggestions.json.
 * 3. If it differs from the current country and is an available country,
 *    load the country data (flag + languages) from the data section.
 *    Same country: only open when the visitor's language (step 4) differs
 *    from the current page language, e.g. a Dutch visitor on the English .com
 *    store is offered "Nederlands".
 * 4. Pick the language: browser language if that market offers it, else the
 *    language matching the country code, else the market's first language.
 * 5. Show the popup in that language with the languages as rows (language flags).
 *    With the currency selector: pick a language + currency (dropdown, faked on
 *    top of countries), then confirm.
 *    Without: one click on a language switches country + language.
 *
 * Strings are kept here (not in locales) because they must be shown in the
 * language the visitor switches TO, not the language of the current page.
 */

if (!customElements.get('localization-popup') && typeof ModalElement !== 'undefined') {
  const STRINGS = {
    nl: {
      heading: 'In welke taal wil je shoppen?',
      stay: 'Blijf op de webshop voor {current}',
      stayLanguage: 'Verder in het {language}',
      currency: 'Valuta',
      confirm: 'Ga verder'
    },
    en: {
      heading: 'Which language would you like to shop in?',
      stay: 'Stay on the {current} store',
      stayLanguage: 'Continue in {language}',
      currency: 'Currency',
      confirm: 'Continue'
    },
    de: {
      heading: 'In welcher Sprache möchtest du shoppen?',
      stay: 'Im Shop für {current} bleiben',
      stayLanguage: 'Weiter auf {language}',
      currency: 'Währung',
      confirm: 'Weiter'
    },
    fr: {
      heading: 'Dans quelle langue souhaitez-vous faire vos achats ?',
      stay: 'Rester sur la boutique {current}',
      stayLanguage: 'Continuer en {language}',
      currency: 'Devise',
      confirm: 'Continuer'
    },
    it: {
      heading: 'In che lingua vuoi fare shopping?',
      stay: 'Resta sul negozio per {current}',
      stayLanguage: 'Continua in {language}',
      currency: 'Valuta',
      confirm: 'Continua'
    },
    es: {
      heading: '¿En qué idioma quieres comprar?',
      stay: 'Quedarse en la tienda de {current}',
      stayLanguage: 'Continuar en {language}',
      currency: 'Moneda',
      confirm: 'Continuar'
    }
  };

  // Languages whose code is not a country code (first entry = flag + preferred market)
  const LANGUAGE_COUNTRIES = { en: ['GB', 'US', 'IE'], da: ['DK'], sv: ['SE'], nb: ['NO'], cs: ['CZ'], el: ['GR'], ja: ['JP'], ko: ['KR'], uk: ['UA'], he: ['IL'], hi: ['IN'], vi: ['VN'], ar: ['AE'], et: ['EE'], sl: ['SI'], sr: ['RS'], ca: ['ES'] };

  customElements.define(
    'localization-popup',
    class LocalizationPopup extends ModalElement {
      constructor() {
        super();

        if (window.location.pathname === '/challenge' || Shopify.designMode) return;

        // Just switched market/language (possibly to another domain, where the
        // cookie of the previous domain does not exist): that was the visitor's
        // choice, so do not suggest another store here either.
        // Flag set by assets/cart-market-transfer.js from ?localization_switched=1.
        if (this.justSwitched && !this.testMode) {
          this.setCookie(this.cookieName, this.expiry);
          return;
        }

        if (!this.testMode && this.getCookie(this.cookieName)) return;

        this.init();
      }

      get shouldLock() {
        return true;
      }

      get shouldCloseAll() {
        return true;
      }

      get cookieName() {
        return 'notbranded:localization-popup';
      }

      get testMode() {
        return this.getAttribute('data-test-mode') === 'true';
      }

      get delay() {
        return parseInt(this.getAttribute('data-delay') || '2', 10);
      }

      get expiry() {
        return parseInt(this.getAttribute('data-expiry') || '30', 10);
      }

      get currentCountry() {
        return this.getAttribute('data-current-country');
      }

      connectedCallback() {
        super.connectedCallback();

        this.querySelector('[data-popup-stay]')?.addEventListener('click', () => this.hide());

        // The mobile header flag and the announcement bar language open this popup.
        // Capture phase on document runs before the theme's own listeners on the
        // trigger (bottom sheet / hover dropdown), so those stay as fallback when
        // this section is disabled.
        if (!LocalizationPopup.triggersBound) {
          LocalizationPopup.triggersBound = true;
          const trigger = (event) => event.target.closest?.('[data-localization-popup-trigger]');

          // Another popup's backdrop covers the page, including the announcement
          // bar / header: the theme newsletter popup or an app popup (Klaviyo).
          // Those show on the first visit of each domain, so right after a market
          // switch. A click on that backdrop exactly on a language button should
          // still open this popup. Backdrop = an element covering (nearly) the
          // whole viewport; this popup's own overlay is excluded (it closes it).
          const coveredTrigger = (event) => {
            if (typeof event.clientX !== 'number' || typeof document.elementsFromPoint !== 'function') return null;
            const target = event.target;
            if (!(target instanceof Element) || target.closest('localization-popup')) return null;

            const rect = target.getBoundingClientRect();
            if (rect.width < window.innerWidth * 0.9 || rect.height < window.innerHeight * 0.9) return null;

            return document.elementsFromPoint(event.clientX, event.clientY)
              .map((element) => element.closest('[data-localization-popup-trigger]'))
              .find(Boolean) || null;
          };

          document.addEventListener('click', (event) => {
            // Always the popup currently in the page: the section can be re-rendered
            // (theme editor), which would leave `this` pointing at a detached element.
            const popup = document.querySelector('localization-popup');
            if (!popup?.openManual) return;

            const element = trigger(event);
            if (element) {
              event.preventDefault();
              event.stopPropagation();
              popup.openManual(element);
              return;
            }

            // Click on a backdrop over a language button: let the click through so
            // that popup closes itself as usual, and open this one as well.
            // Opened after this click is fully handled, so the theme's overlay
            // toggle cannot reopen the other popup after show() closed it.
            const covered = coveredTrigger(event);
            if (covered) setTimeout(() => popup.openManual(covered));
          }, true);

          // Announcement bar dropdown opens on hover; block that while the popup is used
          ['mouseenter', 'mouseleave'].forEach((type) => {
            document.addEventListener(type, (event) => {
              if (!document.querySelector('localization-popup')) return;
              if (event.target.matches?.('[data-localization-popup-trigger]')) event.stopPropagation();
            }, true);
          });
        }

        // In the theme editor: fill the popup with test data when the section is selected
        if (Shopify.designMode && !this.designPreviewBound) {
          this.designPreviewBound = true;
          this.closest('.shopify-section')?.addEventListener('shopify:section:select', () => this.init(true));
        }
      }

      // Opened by the visitor (flag / language in the announcement bar): start
      // from the current country + language, without the "stay" link.
      async openManual(activeElement = null) {
        try {
          const countries = await this.loadCountries();
          const target = countries[this.currentCountry] ? { iso: this.currentCountry, ...countries[this.currentCountry] } : null;
          if (!target) return;

          this.manual = true;
          this.languageOnly = false;
          this.render(target, countries, this.currentLanguage);
          this.show(activeElement);
        } catch (error) {
          console.log('localization-popup:', error);
        }
      }

      async init(preview = false) {
        try {
          const detected = this.testMode || preview ? null : await this.detectCountry();
          this.detectedCountry = detected;
          if (!this.testMode && !preview && !detected) return;

          // Visitor is in the current country: only suggest a better language
          // (a Dutch visitor on the English .com store gets "Nederlands").
          if (!this.testMode && !preview && detected === this.currentCountry) {
            if (this.browserMatchesCurrentLanguage()) return; // no extra request needed

            const countries = await this.loadCountries();
            const current = countries[this.currentCountry];
            if (!current) return;

            const target = { iso: this.currentCountry, ...current };
            const language = this.pickLanguage(target);
            if (!language || this.sameLanguage(language, this.currentLanguage)) return;

            this.manual = false;
            this.languageOnly = true;
            // Preselect the language of the store the visitor is on (market
            // language); the visitor picks their own language from the list.
            this.render(target, countries, this.currentLanguage);
            setTimeout(() => { if (!this.open && !this.manual) this.show(); }, this.delay * 1000);
            return;
          }

          const countries = await this.loadCountries();
          const target = this.pickCountry(countries, detected, this.testMode || preview);
          if (!target) return;

          this.manual = false;
          this.languageOnly = false;
          // Preselect the language of the store the visitor is on (market
          // language, e.g. Deutsch on .de); render() falls back to the visitor's
          // language when the suggested market does not offer it (test mode too).
          this.render(target, countries, this.testMode || preview ? null : this.currentLanguage);

          if (!preview) setTimeout(() => { if (!this.open && !this.manual) this.show(); }, this.delay * 1000);
        } catch (error) {
          console.log('localization-popup:', error);
        }
      }

      async detectCountry() {
        const response = await fetch('/browsing_context_suggestions.json?country[enabled]=true', {
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) return null;
        const data = await response.json();
        return data?.detected_values?.country?.handle?.toUpperCase() || null;
      }

      async loadCountries() {
        if (this.countriesCache) return this.countriesCache;
        // Request the data on the page that is already open: a locale root such
        // as /nl/ on notbranded.com redirects to another market domain
        // (notbranded.nl), and that cross-origin redirect is blocked by CORS.
        const source = new URL(this.getAttribute('data-data-url'), window.location.origin);
        const url = new URL(window.location.pathname, window.location.origin);
        url.search = source.search;
        const response = await fetch(url);
        const html = await response.text();
        const script = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('[data-localization-popup-data]');
        this.countriesCache = script ? JSON.parse(script.textContent) : {};
        return this.countriesCache;
      }

      pickCountry(countries, detected, testing) {
        let iso = detected;

        if (testing) {
          const testCountry = this.getAttribute('data-test-country');
          iso = testCountry && countries[testCountry] ? testCountry : Object.keys(countries).find((code) => code !== this.currentCountry);
        }

        if (!iso || iso === this.currentCountry || !countries[iso]) return null;
        return { iso, ...countries[iso] };
      }

      get justSwitched() {
        if (window.notbrandedLocalizationSwitched) return true;
        // Fallback when cart-market-transfer.js did not run (it removes the param)
        try {
          return new URL(window.location.href).searchParams.has('localization_switched');
        } catch (error) {
          return false;
        }
      }

      get currentLanguage() {
        return (this.getAttribute('data-current-language') || '').toLowerCase();
      }

      sameLanguage(a, b) {
        return Boolean(a && b) && a.toLowerCase().split('-')[0] === b.toLowerCase().split('-')[0];
      }

      // The browser's first language is the page language: pickLanguage() would
      // return the current language, so there is nothing to suggest.
      browserMatchesCurrentLanguage() {
        const first = (navigator.languages || [navigator.language || ''])[0] || '';
        return this.sameLanguage(first, this.currentLanguage);
      }

      pickLanguage(target) {
        const offered = (target.languages || []).map((code) => code.toLowerCase());
        if (!offered.length) return null;

        const browser = (navigator.languages || [navigator.language || ''])
          .map((code) => code.toLowerCase());

        for (const code of browser) {
          if (offered.includes(code)) return code;
          const base = code.split('-')[0];
          const match = offered.find((lang) => lang === base || lang.split('-')[0] === base);
          if (match) return match;
        }

        const sameAsCountry = offered.find((lang) => lang.split('-')[0] === target.iso.toLowerCase());
        return sameAsCountry || offered[0];
      }

      countryName(iso, language, fallback) {
        try {
          return new Intl.DisplayNames([language], { type: 'region' }).of(iso) || fallback;
        } catch (error) {
          return fallback;
        }
      }

      get countrySelector() {
        return this.getAttribute('data-country-selector') === 'true';
      }

      render(target, countries, language = null) {
        this.target = target;
        this.countries = countries;
        language = language || this.pickLanguage(target) || 'en';

        // Currency mode: languages = every language offered by a country with this currency
        const currencyLanguages = this.countrySelector ? this.languagesForCurrency(target.currency, target) : null;
        if (currencyLanguages?.length && !currencyLanguages.includes(language)) {
          language = this.pickLanguage(target) || currencyLanguages[0];
        }
        this.language = language;
        const strings = STRINGS[language.split('-')[0]] || STRINGS.en;

        const current = countries[this.currentCountry];
        const vars = {
          country: this.countryName(target.iso, language, target.name),
          current: this.countryName(this.currentCountry, language, current?.name || this.currentCountry),
          language: this.languageNameIn(this.currentLanguage, language)
        };
        const fill = (text) => text.replace(/\{(\w+)\}/g, (_, key) => vars[key] || '');

        this.setAttribute('lang', language);
        this.querySelector('[data-popup-heading]').textContent = fill(strings.heading);
        const stay = this.querySelector('[data-popup-stay]');
        stay.textContent = fill(this.languageOnly && strings.stayLanguage ? strings.stayLanguage : strings.stay);
        stay.hidden = Boolean(this.manual);

        if (this.countrySelector) {
          this.querySelector('[data-popup-currency-label]').textContent = strings.currency;
          this.querySelector('[data-popup-confirm]').textContent = strings.confirm;
          this.querySelector('[data-popup-country]').value = this.countryFor(target.currency, language, target);
          this.querySelector('[data-popup-locale]').value = language;
          this.renderLanguageChoices(currencyLanguages, language, target);
          this.renderCurrencySelect(target, language);
        } else {
          this.renderOptions(target, countries, language);
        }
      }

      // Currency mode: all languages available in the selected currency are
      // selectable rows; choosing one only selects it (and translates the popup).
      // The confirm button switches to countryFor(currency, language).
      renderLanguageChoices(languages, active, target) {
        const list = this.querySelector('[data-popup-options]');
        list.innerHTML = '';

        languages.forEach((lang) => {
          const item = document.createElement('li');
          const country = this.countries[this.countryFor(target.currency, lang, target)] || target;
          const button = this.languageButton(lang, country);
          button.setAttribute('aria-pressed', lang === active ? 'true' : 'false');
          if (lang === active) button.classList.add('localization-popup__option--suggested');
          button.addEventListener('click', () => {
            this.render(this.target, this.countries, lang);
            this.querySelector(`[data-popup-options] [data-lang="${lang}"]`)?.focus();
          });
          item.appendChild(button);
          list.appendChild(item);
        });
      }

      // Unique languages of all countries using `currency`; the selected
      // country's languages first, in their own order.
      languagesForCurrency(currency, target) {
        const result = [];
        const add = (code) => {
          const lang = code.toLowerCase();
          if (!result.includes(lang)) result.push(lang);
        };

        (target.languages || []).forEach(add);
        Object.values(this.countries)
          .filter((country) => country.currency === currency)
          .forEach((country) => (country.languages || []).forEach(add));

        return result;
      }

      // Country to switch to for a currency + language: the selected country,
      // the visitor's detected country or the current country when they offer
      // both; else the language's own country (en → GB/IE, de → DE); else any
      // country with that currency and language.
      countryFor(currency, language, anchor) {
        const offers = (iso) => {
          const country = this.countries[iso];
          return country?.currency === currency
            && (country.languages || []).some((code) => code.toLowerCase() === language);
        };

        // A language goes to its own country first (nl → NL, de → DE), so
        // picking Nederlands on notbranded.com lands on the NL market (.nl).
        // Only a visitor who is really in the anchor country (a Belgian
        // choosing Nederlands) stays there.
        const [base, region] = language.split('-');
        const native = (region || base).toUpperCase();
        const keepAnchor = anchor?.iso && anchor.iso === this.detectedCountry;
        const candidates = [
          keepAnchor ? anchor.iso : null,
          native,
          anchor?.iso,
          this.detectedCountry,
          this.currentCountry,
          ...(LANGUAGE_COUNTRIES[base] || []),
          (region || base).toUpperCase()
        ];

        return candidates.find((iso) => iso && offers(iso))
          || Object.keys(this.countries).find(offers)
          || anchor?.iso;
      }

      // Currency selector (faked on top of countries): one option per unique
      // currency. Picking a currency switches to the most logical country that
      // uses it; see countryForCurrency().
      renderCurrencySelect(target, language) {
        const select = this.querySelector('[data-popup-currency-select]');
        if (!select) return;

        const currencies = new Map();
        Object.values(this.countries).forEach(({ currency, symbol }) => {
          if (currency && !currencies.has(currency)) currencies.set(currency, symbol);
        });

        select.innerHTML = '';
        [...currencies.entries()]
          .sort(([a], [b]) => a.localeCompare(b))
          .forEach(([currency, symbol]) => {
            const option = document.createElement('option');
            option.value = currency;
            option.textContent = symbol && symbol !== currency ? `${currency} ${symbol}` : currency;
            option.selected = currency === target.currency;
            select.appendChild(option);
          });

        if (!select.dataset.bound) {
          select.dataset.bound = 'true';
          select.addEventListener('change', () => {
            const iso = this.countryForCurrency(select.value, this.target, this.language);
            if (!iso) return;
            // render() keeps the selected language when any country with this
            // currency offers it, otherwise picks the best one for the new country
            this.render({ iso, ...this.countries[iso] }, this.countries, this.language);
          });
        }
      }

      // Most logical country for a currency, in this order:
      // 1. the suggested/selected country, if it already uses the currency
      // 2. the detected country of the visitor, if it uses the currency
      // 3. the current country, if it uses the currency
      // 4. a country with that currency that offers the selected language,
      //    preferring the language's own country (en → GB, de → DE)
      // 5. the first country with that currency
      countryForCurrency(currency, selected, language) {
        const entries = Object.entries(this.countries).filter(([, country]) => country.currency === currency);
        if (!entries.length) return null;

        const uses = (iso) => this.countries[iso]?.currency === currency;
        if (selected && uses(selected.iso)) return selected.iso;
        if (this.detectedCountry && uses(this.detectedCountry)) return this.detectedCountry;
        if (uses(this.currentCountry)) return this.currentCountry;

        const offersLanguage = ([, country]) => (country.languages || []).some((code) => code.toLowerCase() === language);
        const preferred = (LANGUAGE_COUNTRIES[language.split('-')[0]] || []).concat((language.split('-')[1] || language).toUpperCase());
        const own = entries.find(([iso, country]) => preferred.includes(iso) && offersLanguage([iso, country]));
        if (own) return own[0];

        const withLanguage = entries.find(offersLanguage);
        return (withLanguage || entries[0])[0];
      }

      languageButton(lang, country) {
        const button = document.createElement('button');
        button.type = 'button';
        // Language code for the script; no lang attribute: the visible name is in
        // the popup language (Niederländisch on .de), which the popup's lang covers.
        button.dataset.lang = lang;
        button.className = 'localization-popup__option';

        // Flag shows the LANGUAGE (Nederlands = NL, Français = FR); the store
        // country is set separately (detected country or the country dropdown).
        const flag = document.createElement('span');
        flag.className = 'header-localization__flag';
        const img = document.createElement('img');
        img.src = this.languageFlag(lang) || country.flag;
        img.alt = '';
        img.width = 18;
        img.height = 18;
        img.loading = 'lazy';
        img.addEventListener('error', () => {
          if (country.flag && img.src !== country.flag) img.src = country.flag;
        }, { once: true });
        flag.appendChild(img);

        const name = document.createElement('span');
        name.className = 'localization-popup__option-name';
        name.textContent = this.displayLanguageName(lang);

        button.append(flag, name);
        return button;
      }

      // Without country selector: one row per language (all markets); one click
      // switches. Languages of the suggested country come first and stay in that
      // country; the others go to their own market (nl → NL, en → GB).
      renderOptions(target, countries, suggested) {
        const list = this.querySelector('[data-popup-options]');
        if (!list) return;

        const options = [];
        const seen = new Set();
        const add = (lang, iso) => {
          const code = lang.toLowerCase();
          if (seen.has(code) || !iso || !countries[iso]) return;
          seen.add(code);
          options.push({ lang: code, iso });
        };

        const targetLanguages = (target.languages || []).map((code) => code.toLowerCase());
        if (targetLanguages.includes(suggested)) add(suggested, target.iso);
        targetLanguages.forEach((lang) => add(lang, target.iso));

        Object.values(countries).forEach((country) => {
          (country.languages || []).forEach((lang) => add(lang, this.marketForLanguage(lang.toLowerCase(), countries)));
        });

        list.innerHTML = '';
        options.forEach(({ lang, iso }, index) => {
          const item = document.createElement('li');
          const button = this.languageButton(lang, countries[iso]);
          if (index === 0) button.classList.add('localization-popup__option--suggested');
          button.addEventListener('click', () => this.submitChoice(iso, lang));
          item.appendChild(button);
          list.appendChild(item);
        });
      }

      // Flag that represents a language: nl → NL, fr → FR, en → GB, pt-BR → BR
      languageFlag(lang) {
        const [base, region] = lang.split('-');
        const code = (region || (LANGUAGE_COUNTRIES[base] || [])[0] || base).toLowerCase();
        return /^[a-z]{2}$/.test(code) ? `https://cdn.shopify.com/static/images/flags/${code}.svg` : null;
      }

      // Same rules as snippets/custom-header-localization-language.liquid
      marketForLanguage(lang, countries) {
        const DEFAULTS = LANGUAGE_COUNTRIES;
        const offers = (iso) => countries[iso]?.languages?.some((code) => code.toLowerCase() === lang);
        const [base, region] = lang.split('-');
        const candidates = [...(DEFAULTS[base] || []), (region || base).toUpperCase(), this.currentCountry];

        return candidates.find(offers) || Object.keys(countries).find(offers) || null;
      }

      submitChoice(iso, lang) {
        const form = this.querySelector('form');
        this.querySelector('[data-popup-country]').value = iso;
        this.querySelector('[data-popup-locale]').value = lang;
        // requestSubmit fires a submit event, so cart-market-transfer.js can carry the cart over
        if (form.requestSubmit) form.requestSubmit();
        else form.submit();
      }

      // Name of `code` written in `displayLanguage` (English in Dutch = "Engels")
      languageNameIn(code, displayLanguage) {
        if (!code) return '';
        try {
          return new Intl.DisplayNames([displayLanguage], { type: 'language' }).of(code) || code;
        } catch (error) {
          return code.toUpperCase();
        }
      }

      // Name of `code` in the popup language (on .de: Niederländisch, Englisch),
      // capitalised for the list (fr: « Néerlandais », not « néerlandais »).
      displayLanguageName(code) {
        const display = this.language || code;
        const name = this.languageNameIn(code, display);
        if (!name || name === code.toUpperCase()) return this.languageName(code);
        return name.charAt(0).toLocaleUpperCase(display) + name.slice(1);
      }

      languageName(code) {
        try {
          const name = new Intl.DisplayNames([code], { type: 'language' }).of(code) || code;
          return name.charAt(0).toLocaleUpperCase(code) + name.slice(1);
        } catch (error) {
          return code.toUpperCase();
        }
      }

      afterHide() {
        super.afterHide();
        if (!this.testMode) this.setCookie(this.cookieName, this.expiry);
      }

      getCookie(name) {
        const match = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
        return match ? match[2] : null;
      }

      setCookie(name, expiry) {
        document.cookie = `${name}=true; max-age=${expiry * 24 * 60 * 60}; path=/`;
      }
    }
  );
}
